import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { toast } from '@/features/toast/toast'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import { track, TrackType } from '@/utils/tracking/track'
import type {
    OrganizationCreateDirectoryData,
    OrganizationCreateDirectoryPayload,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/nodes/create-directory'
import type { NodeType as BackendNodeType } from 'database'

type IFileVideoMetadata = {
    codec: string
    duration: number
    hasAudio: boolean
    bitrate: number | null
    height: number
    width: number
    format: string
}

interface IFileAudioMetadata {
    title?: string
    artist?: unknown[]
    albumArtist?: unknown[]
    album?: string
    year?: number
    track?: { no: number; of: number }
    genre?: unknown[]
    disk?: { no: number; of: number }
    picture?: any[]
    duration: number
}

export type INode = NonNullable<OrganizationCreateDirectoryData> & {
    systemNode?: boolean
}

export type IFile = Omit<NonNullable<INode['file']>, 'videoMetadata' | 'audioMetadata'> & {
    videoMetadata?: unknown | IFileVideoMetadata
    audioMetadata?: unknown | IFileAudioMetadata
}

export enum NodeType {
    DIRECTORY = 'DIRECTORY',
    FILE = 'FILE',
    STUDIO = 'STUDIO',
}
export type INodeType = NodeType

// Type check: ensure NodeType is an exact copy of BackendNodeType
// Will error at compile time if backend adds/removes values
type _NodeTypeValues = `${NodeType}`
type _MissingInLocal = Exclude<BackendNodeType, _NodeTypeValues>
type _ExtraInLocal = Exclude<_NodeTypeValues, BackendNodeType>
type _AssertNever<T extends never> = T
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _CheckNodeType = _AssertNever<_MissingInLocal> & _AssertNever<_ExtraInLocal>

export const assertIsVideoMetadata = (data: unknown): data is IFileVideoMetadata => {
    if (typeof data !== 'object' || data === null) {
        return false
    }

    const { duration } = data as Record<string, unknown>

    return typeof duration === 'number'
}

export const assertIsAudioMetadata = (data: unknown): data is IFileAudioMetadata => {
    if (typeof data !== 'object' || data === null) {
        return false
    }

    const { duration } = data as Record<string, unknown>

    return typeof duration === 'number'
}

export type IFileCategory = NonNullable<INode['file']>['category']

export enum FileCategory {
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
    AUDIO = 'AUDIO',
    DATA = 'DATA',
    FONT = 'FONT',
}

FileCategory satisfies { [key: string]: IFileCategory }

type ICreateDirectoryParams = {
    organizationId: string
    brandSlug: string
} & OrganizationCreateDirectoryPayload

type ICreateDirectoryPayload = Pick<ICreateDirectoryParams, 'name' | 'parentNodePath' | 'emoji' | 'color'>

export class CreateDirectoryMutation {
    static call(api: IApi, { organizationId, brandSlug, ...params }: ICreateDirectoryParams) {
        return api.post<INode, ICreateDirectoryPayload>(
            `/organization/${organizationId}/brands/${brandSlug}/nodes/create-directory`,
            params
        )
    }
}

export const useCreateDirectoryMutation = () => {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: (payload: ICreateDirectoryPayload) => {
            return CreateDirectoryMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            }).then(async (res) => {
                // Invalidation promise must be returned
                await qc.invalidateQueries({
                    predicate: (query) => {
                        const matched = NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key)

                        return matched
                    },
                })
                return res.data
            })
        },
        onSuccess(data) {
            track(TrackType.DIRECTORY_CREATED, { name: data.name, path: data.path })
        },
        onError(error) {
            toast.firstApiError(error, 'Failed to create directory')
        },
    })
}
